/**
 * useCalibrationFilterBuilder Hook
 * 
 * Pure logic for building calibration filter parameters.
 * Provides builder functions for different calibration scenarios.
 */

import { useCallback } from 'react';
import moment from 'moment';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import type { ICalibrationAvailability } from '../types/calibration';
import {
  generateDynamicFilterConfig,
  getDynamicVisualizationMetrics,
} from '../utils/dynamicMetricMapping';
import { DATABRAIN_UNITS_METRICS_SALES_SEGMENT_MAPPING } from '../const';
import { CALIBRATION_METRICS } from '../utils';
import { getSinceReleaseTimeOfRangePicker } from '@intelligence/utils/topchartsUtils';
import { getAllValuesOfCascader } from '../utils';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
} from '../components/MetricsSelection/config';

interface UseCalibrationFilterBuilderParams {
  calibrationFlags: ICalibrationAvailability | null;
  releaseTime: string;
}

interface UseCalibrationFilterBuilderResult {
  buildCalibrationFilters: (metric: string, currentFilterValue: any) => any;
  buildSwitcherFilters: (currentFilterValue: any, defaultInputList: any[]) => any;
}

/**
 * Hook to build calibration filter parameters
 * 
 * Provides two builder functions:
 * 1. buildCalibrationFilters - For "View calibration data" tooltip clicks
 * 2. buildSwitcherFilters - For "Switch to Calibration Metrics" button clicks
 * 
 * @param calibrationFlags - Parsed calibration flags
 * @param releaseTime - Game release time
 * @returns Builder functions for filter construction
 * 
 * @example
 * ```typescript
 * const { buildCalibrationFilters, buildSwitcherFilters } = useCalibrationFilterBuilder({
 *   calibrationFlags,
 *   releaseTime: '2023-01-01'
 * });
 * 
 * const filters = buildCalibrationFilters('databrain__total_revenue', filterValue);
 * ```
 */
export function useCalibrationFilterBuilder(
  params: UseCalibrationFilterBuilderParams
): UseCalibrationFilterBuilderResult {
  const { calibrationFlags, releaseTime } = params;

  /**
   * Build filter parameters for viewing calibration data
   * Used when clicking "View calibration raw data" in metric explanation tooltip
   */
  const buildCalibrationFilters = useCallback(
    (metric: string, currentFilterValue: any) => {
      const filterConfig = generateDynamicFilterConfig(metric, calibrationFlags);
      const defaultMetrics = filterConfig.metricsToSelect;
      const defaultSalesSegments = DATABRAIN_UNITS_METRICS_SALES_SEGMENT_MAPPING[metric];
      const sinceReleaseTime = getSinceReleaseTimeOfRangePicker(releaseTime);
      const currentDate = moment().format('YYYY-MM-DD');

      return {
        ...currentFilterValue,
        rangeTime: {
          type: 'date',
          value: [sinceReleaseTime, currentDate],
        },
        cascader: {
          value: { ...defaultSalesSegments },
        },
        metric_selection_for_trends: {
          value: {
            ...currentFilterValue?.metric_selection_for_trends?.value,
            metrics: defaultMetrics,
            type: 'date',
            selectedMetricsValue: 'metrics',
            shotSelectValue: 'all',
            selectCutVal: 'all',
            types: 'all',
            sources: ['all'],
          },
        },
      };
    },
    [calibrationFlags, releaseTime]
  );

  /**
   * Build filter parameters for switching to calibration mode
   * Used when clicking "Switch to Calibration Metrics" button
   */
  const buildSwitcherFilters = useCallback(
    (currentFilterValue: any, defaultInputList: any[]) => {
      // Get cascader options to determine all segment values
      const cascaderConfig = defaultInputList.find(
        (item) => item.key === filterKeyMap.CASCADER
      );
      const cascaderOptions = (cascaderConfig?.options as any)?.children || [];
      const allSegmentValues = getAllValuesOfCascader(cascaderOptions);

      // Get metric configuration
      const sourceMetrics = getSourceMetricsByValue(CALIBRATION_METRICS);
      const metricsOptions = getMetricOptionByValue(CALIBRATION_METRICS);

      return {
        ...currentFilterValue,
        [filterKeyMap.RANGE_TIME]: {
          type: 'day',
          value: currentFilterValue?.[filterKeyMap.RANGE_TIME]?.value || ['', ''],
        },
        [filterKeyMap.REGION]: {
          ...currentFilterValue?.[filterKeyMap.REGION],
          regions: ['global'],
          markets: ['global'],
          type: 'market',
        },
        [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
          value: {
            ...currentFilterValue?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value,
            selectedMetricsValue: 'dataSource',
            shotSelectValue: 'databrain',
            selectCutVal: 'all',
            type: 'daily',
            types: 'all',
            sources: ['all'],
            metrics: CALIBRATION_METRICS,
            source_metrics: sourceMetrics,
            metricsOptions,
            metricsOptionsByConfigOrder: metricsOptions,
          },
        },
        [filterKeyMap.CASCADER]: {
          value: allSegmentValues,
        },
      };
    },
    []
  );

  return { buildCalibrationFilters, buildSwitcherFilters };
}
